import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AccountBalance, AccountBalanceWallet, Receipt, CreditCard } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { dailyAccountAPI } from '../services/api';
import { Store, DailyAccount, DenominationInput, CashTallyItem, CashTallyData, BankExpenseItem, BankExpenseData } from '../types';

interface DailyAccountProps {
  selectedStore?: Store | null;
}

const DailyAccountComponent: React.FC<DailyAccountProps> = ({ selectedStore }) => {
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [storeId, setStoreId] = useState<number | ''>('');
  const [currentAccount, setCurrentAccount] = useState<DailyAccount | null>(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Opening Balance Dialog
  const [openingBalanceDialog, setOpeningBalanceDialog] = useState(false);
  const [openingDenominations, setOpeningDenominations] = useState<DenominationInput[]>([
    { denomination: 2000, count: 0, total: 0 },
    { denomination: 1000, count: 0, total: 0 },
    { denomination: 500, count: 0, total: 0 },
    { denomination: 200, count: 0, total: 0 },
    { denomination: 100, count: 0, total: 0 },
    { denomination: 50, count: 0, total: 0 },
    { denomination: 20, count: 0, total: 0 },
    { denomination: 10, count: 0, total: 0 },
    { denomination: 5, count: 0, total: 0 },
    { denomination: 2, count: 0, total: 0 },
    { denomination: 1, count: 0, total: 0 },
  ]);

  // Closing Balance Dialog
  const [closingBalanceDialog, setClosingBalanceDialog] = useState(false);
  const [closingDenominations, setClosingDenominations] = useState<DenominationInput[]>([
    { denomination: 2000, count: 0, total: 0 },
    { denomination: 1000, count: 0, total: 0 },
    { denomination: 500, count: 0, total: 0 },
    { denomination: 200, count: 0, total: 0 },
    { denomination: 100, count: 0, total: 0 },
    { denomination: 50, count: 0, total: 0 },
    { denomination: 20, count: 0, total: 0 },
    { denomination: 10, count: 0, total: 0 },
    { denomination: 5, count: 0, total: 0 },
    { denomination: 2, count: 0, total: 0 },
    { denomination: 1, count: 0, total: 0 },
  ]);

  // Cash Tally Dialog
  const [cashTallyDialog, setCashTallyDialog] = useState(false);
  const [cashTallyItems, setCashTallyItems] = useState<CashTallyItem[]>([]);
  const [selectedCreditType, setSelectedCreditType] = useState<string>('');
  const [selectedDebitType, setSelectedDebitType] = useState<string>('');
  const [creditAmount, setCreditAmount] = useState<number>(0);
  const [debitAmount, setDebitAmount] = useState<number>(0);

  // Bank Expense Dialog
  const [bankExpenseDialog, setBankExpenseDialog] = useState(false);
  const [bankExpenseItems, setBankExpenseItems] = useState<BankExpenseItem[]>([]);
  const [selectedExpenseType, setSelectedExpenseType] = useState<string>('');
  const [expenseAmount, setExpenseAmount] = useState<number>(0);

  // Cash Credit Types (Green)
  const cashCreditTypes = [
    'cash_sale',
    'candy_misc_sales',
    'customer_old_settlement',
    'purchase_return_cash_settlement',
    'gift_cash',
    'cash_withdrawn_from_bank',
    'discount_cash_received',
    'staff_advance_return',
    'expense_refund_cash',
    'round_off_gain_cash',
    'cash_received_from_other_store',
    'stationary_return',
    'other_cash_received',
    'excess_in_calculation'
  ];

  // Cash Debit Types (Red)
  const cashDebitTypes = [
    'tea_snacks',
    'lunch',
    'overtime_salary',
    'salary_cash',
    'purchase_cash',
    'sales_return_cash',
    'stationary_purchase',
    'building_rent',
    'computer_repair',
    'store_maintenance_repair',
    'bike_repair',
    'fuel_expense',
    'courier',
    'utility_bill_cash',
    'advertisement_cash',
    'misc_expense_cash',
    'bank_deposit_cash',
    'loan_repayment_cash',
    'decoration_expense_cash',
    'tax_payment_cash',
    'other_payment',
    'missing_cash'
  ];

  // Bank/Cheque Expense Types
  const bankExpenseTypes = [
    'lunch',
    'overtime_salary',
    'salary',
    'purchase',
    'sales_return',
    'stationary_purchase',
    'building_rent',
    'computer_repair',
    'store_maintenance_repair',
    'bike_repair',
    'fuel_expense',
    'courier',
    'utility_bill',
    'advertisement',
    'misc_expense',
    'loan_repayment',
    'decoration_expense',
    'tax_payment',
    'other_payment'
  ];

  // Set store ID when selectedStore is available
  useEffect(() => {
    if (selectedStore) {
      setStoreId(selectedStore.id);
    }
  }, [selectedStore]);

  // Load daily account when date or store changes
  useEffect(() => {
    if (storeId && date) {
      loadDailyAccount();
    }
  }, [storeId, date]);

  const loadDailyAccount = async () => {
    if (!storeId || !date) return;
    
    setLoading(true);
    try {
      const account = await dailyAccountAPI.getByDateAndStore(date.format('YYYY-MM-DD'), storeId);
      setCurrentAccount(account);
    } catch (error) {
      console.error('Error loading daily account:', error);
      setAlert({ type: 'error', message: 'Failed to load daily account' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpeningBalanceClick = () => {
    // Pre-fill with existing opening balance denominations if available
    if (currentAccount && currentAccount.opening_cash_denomination) {
      try {
        const existingDenominations = JSON.parse(currentAccount.opening_cash_denomination);
        setOpeningDenominations(existingDenominations);
      } catch (error) {
        console.error('Error parsing opening denominations:', error);
        // Fallback to simple distribution if parsing fails
        const total = currentAccount.opening_cash;
        const newDenominations = [...openingDenominations];
        
        let remaining = total;
        for (let i = 0; i < newDenominations.length && remaining > 0; i++) {
          const count = Math.floor(remaining / newDenominations[i].denomination);
          newDenominations[i].count = count;
          newDenominations[i].total = count * newDenominations[i].denomination;
          remaining -= newDenominations[i].total;
        }
        
        setOpeningDenominations(newDenominations);
      }
    } else if (currentAccount) {
      // Fallback to simple distribution if no denominations stored
      const total = currentAccount.opening_cash;
      const newDenominations = [...openingDenominations];
      
      let remaining = total;
      for (let i = 0; i < newDenominations.length && remaining > 0; i++) {
        const count = Math.floor(remaining / newDenominations[i].denomination);
        newDenominations[i].count = count;
        newDenominations[i].total = count * newDenominations[i].denomination;
        remaining -= newDenominations[i].total;
      }
      
      setOpeningDenominations(newDenominations);
    }
    setOpeningBalanceDialog(true);
  };

  const handleClosingBalanceClick = () => {
    // Pre-fill with existing closing balance denominations if available
    if (currentAccount && currentAccount.closing_cash_denomination) {
      try {
        const existingDenominations = JSON.parse(currentAccount.closing_cash_denomination);
        setClosingDenominations(existingDenominations);
      } catch (error) {
        console.error('Error parsing closing denominations:', error);
        // Fallback to simple distribution if parsing fails
        const total = currentAccount.closing_cash;
        const newDenominations = [...closingDenominations];
        
        let remaining = total;
        for (let i = 0; i < newDenominations.length && remaining > 0; i++) {
          const count = Math.floor(remaining / newDenominations[i].denomination);
          newDenominations[i].count = count;
          newDenominations[i].total = count * newDenominations[i].denomination;
          remaining -= newDenominations[i].total;
        }
        
        setClosingDenominations(newDenominations);
      }
    } else if (currentAccount) {
      // Fallback to simple distribution if no denominations stored
      const total = currentAccount.closing_cash;
      const newDenominations = [...closingDenominations];
      
      let remaining = total;
      for (let i = 0; i < newDenominations.length && remaining > 0; i++) {
        const count = Math.floor(remaining / newDenominations[i].denomination);
        newDenominations[i].count = count;
        newDenominations[i].total = count * newDenominations[i].denomination;
        remaining -= newDenominations[i].total;
      }
      
      setClosingDenominations(newDenominations);
    }
    setClosingBalanceDialog(true);
  };

  const updateDenominationCount = (index: number, count: number, isOpening: boolean) => {
    const denominations = isOpening ? openingDenominations : closingDenominations;
    const setDenominations = isOpening ? setOpeningDenominations : setClosingDenominations;
    
    const newDenominations = [...denominations];
    newDenominations[index].count = count;
    newDenominations[index].total = count * newDenominations[index].denomination;
    setDenominations(newDenominations);
  };

  const getTotal = (denominations: DenominationInput[]) => {
    return denominations.reduce((sum, item) => sum + item.total, 0);
  };

  const parseDenominations = (denominationJson: string | null | undefined): DenominationInput[] => {
    if (!denominationJson) return [];
    try {
      return JSON.parse(denominationJson);
    } catch (error) {
      console.error('Error parsing denominations:', error);
      return [];
    }
  };

  const formatDenominations = (denominations: DenominationInput[]): string => {
    if (denominations.length === 0) return 'No denominations recorded';
    
    return denominations
      .filter(d => d.count > 0)
      .map(d => `${d.count} × ₹${d.denomination.toLocaleString()}`)
      .join(', ');
  };

  const handleCashTallyClick = () => {
    // Load existing cash tally if available
    if (currentAccount && currentAccount.cash_tally) {
      try {
        const existingTally = JSON.parse(currentAccount.cash_tally);
        setCashTallyItems(existingTally.items || []);
      } catch (error) {
        console.error('Error parsing cash tally:', error);
        setCashTallyItems([]);
      }
    } else {
      setCashTallyItems([]);
    }
    setCashTallyDialog(true);
  };

  const handleBankExpenseClick = () => {
    // Load existing bank expenses if available
    if (currentAccount && currentAccount.bank_expense) {
      try {
        const existingExpenses = JSON.parse(currentAccount.bank_expense);
        setBankExpenseItems(existingExpenses.items || []);
      } catch (error) {
        console.error('Error parsing bank expenses:', error);
        setBankExpenseItems([]);
      }
    } else {
      setBankExpenseItems([]);
    }
    setBankExpenseDialog(true);
  };

  const addCreditItem = () => {
    if (selectedCreditType && creditAmount > 0) {
      const newItem: CashTallyItem = {
        type: 'credit',
        category: selectedCreditType,
        amount: creditAmount
      };
      setCashTallyItems([...cashTallyItems, newItem]);
      setSelectedCreditType('');
      setCreditAmount(0);
    }
  };

  const addDebitItem = () => {
    if (selectedDebitType && debitAmount > 0) {
      const newItem: CashTallyItem = {
        type: 'debit',
        category: selectedDebitType,
        amount: debitAmount
      };
      setCashTallyItems([...cashTallyItems, newItem]);
      setSelectedDebitType('');
      setDebitAmount(0);
    }
  };

  const removeTallyItem = (index: number) => {
    const newItems = [...cashTallyItems];
    newItems.splice(index, 1);
    setCashTallyItems(newItems);
  };

  const addExpenseItem = () => {
    if (selectedExpenseType && expenseAmount > 0) {
      const newItem: BankExpenseItem = {
        category: selectedExpenseType,
        amount: expenseAmount
      };
      setBankExpenseItems([...bankExpenseItems, newItem]);
      setSelectedExpenseType('');
      setExpenseAmount(0);
    }
  };

  const removeExpenseItem = (index: number) => {
    const newItems = [...bankExpenseItems];
    newItems.splice(index, 1);
    setBankExpenseItems(newItems);
  };

  const calculateCashTallyTotal = (): CashTallyData => {
    const totalCredit = cashTallyItems
      .filter(item => item.type === 'credit')
      .reduce((sum, item) => sum + item.amount, 0);
    
    const totalDebit = cashTallyItems
      .filter(item => item.type === 'debit')
      .reduce((sum, item) => sum + item.amount, 0);
    
    const netAmount = totalCredit - totalDebit;
    
    return {
      items: cashTallyItems,
      total_credit: totalCredit,
      total_debit: totalDebit,
      net_amount: netAmount
    };
  };

  const calculateBankExpenseTotal = (): BankExpenseData => {
    const totalBankExpense = bankExpenseItems.reduce((sum, item) => sum + item.amount, 0);
    
    return {
      items: bankExpenseItems,
      total_bank_expense: totalBankExpense
    };
  };

  const getExpectedDifference = (): number => {
    if (!currentAccount) return 0;
    return currentAccount.closing_cash - currentAccount.opening_cash;
  };

  const calculateTotalExpenses = (cashTallyData?: string, bankExpenseData?: string) => {
    // Calculate cash debit expenses (excluding purchase_cash and bank_deposit_cash)
    let cashDebitExpenses = 0;
    if (cashTallyData) {
      try {
        const tally = JSON.parse(cashTallyData);
        const allDebitItems = tally.items.filter((item: any) => item.type === 'debit');
        const expenseItems = allDebitItems.filter((item: any) => item.category !== 'purchase_cash' && item.category !== 'bank_deposit_cash');
        
        console.log('Cash Tally Analysis:', {
          allDebitItems: allDebitItems.map((item: any) => ({ category: item.category, amount: item.amount })),
          expenseItems: expenseItems.map((item: any) => ({ category: item.category, amount: item.amount })),
          excludedItems: allDebitItems.filter((item: any) => item.category === 'purchase_cash' || item.category === 'bank_deposit_cash')
        });
        
        cashDebitExpenses = expenseItems.reduce((sum: number, item: any) => sum + item.amount, 0);
      } catch (error) {
        console.error('Error parsing cash tally for expense calculation:', error);
      }
    }
    
    // Calculate bank expenses
    let bankExpenses = 0;
    if (bankExpenseData) {
      try {
        const expense = JSON.parse(bankExpenseData);
        bankExpenses = expense.total_bank_expense || 0;
      } catch (error) {
        console.error('Error parsing bank expense for calculation:', error);
      }
    }
    
    const totalExpense = cashDebitExpenses + bankExpenses;
    console.log('Fresh Total Expense Calculation:', {
      cashDebitExpenses,
      bankExpenses,
      totalExpense
    });
    
    return totalExpense;
  };

  const handleCashTallySubmit = async () => {
    const tallyData = calculateCashTallyTotal();
    const expectedDifference = getExpectedDifference();
    
    try {
      if (currentAccount) {
        console.log('Current account before update:', {
          currentTotalExpense: currentAccount.total_expense,
          currentBankExpense: currentAccount.bank_expense
        });
        
        // Recalculate total expense considering both cash debits and existing bank expenses
        const newTotalExpense = calculateTotalExpenses(
          JSON.stringify(tallyData),
          currentAccount.bank_expense
        );
        
        console.log('About to update with newTotalExpense:', newTotalExpense);
        
        // Update existing account
        const updateResult = await dailyAccountAPI.update(currentAccount.id!, {
          cash_tally: JSON.stringify(tallyData),
          total_expense: newTotalExpense,
          updated_by: 'system'
        });
        console.log('Update result from API:', updateResult);
      } else {
        // Create new account if none exists - recalculate from zero
        const newTotalExpense = calculateTotalExpenses(
          JSON.stringify(tallyData),
          undefined // no bank expenses yet
        );
          
        await dailyAccountAPI.create({
          date: date.format('YYYY-MM-DD'),
          store_id: storeId as number,
          opening_cash: 0,
          opening_bank_balance: 0,
          total_expense: newTotalExpense,
          closing_bank_balance: 0,
          total_bank_credits: 0,
          total_bank_debits: 0,
          closing_cash: 0,
          opening_cash_denomination: undefined,
          closing_cash_denomination: undefined,
          cash_tally: JSON.stringify(tallyData),
          created_by: 'system'
        });
      }
      
      setAlert({ type: 'success', message: 'Cash tally saved successfully' });
      setCashTallyDialog(false);
      loadDailyAccount(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving cash tally:', error);
      setAlert({ type: 'error', message: 'Failed to save cash tally' });
    }
  };

  const handleBankExpenseSubmit = async () => {
    const expenseData = calculateBankExpenseTotal();
    
    try {
      if (currentAccount) {
        console.log('Current account before bank expense update:', {
          currentTotalExpense: currentAccount.total_expense,
          currentCashTally: currentAccount.cash_tally
        });
        
        // Recalculate total expense considering both bank expenses and existing cash debits
        const newTotalExpense = calculateTotalExpenses(
          currentAccount.cash_tally,
          JSON.stringify(expenseData)
        );
        
        console.log('About to update with newTotalExpense (bank):', newTotalExpense);
        
        // Update existing account
        await dailyAccountAPI.update(currentAccount.id!, {
          bank_expense: JSON.stringify(expenseData),
          total_expense: newTotalExpense,
          updated_by: 'system'
        });
      } else {
        // Create new account if none exists - recalculate from zero
        const newTotalExpense = calculateTotalExpenses(
          undefined, // no cash tally yet
          JSON.stringify(expenseData)
        );
        
        await dailyAccountAPI.create({
          date: date.format('YYYY-MM-DD'),
          store_id: storeId as number,
          opening_cash: 0,
          opening_bank_balance: 0,
          total_expense: newTotalExpense,
          closing_bank_balance: 0,
          total_bank_credits: 0,
          total_bank_debits: 0,
          closing_cash: 0,
          opening_cash_denomination: undefined,
          closing_cash_denomination: undefined,
          cash_tally: undefined,
          bank_expense: JSON.stringify(expenseData),
          created_by: 'system'
        });
      }
      
      setAlert({ type: 'success', message: 'Bank expenses saved successfully' });
      setBankExpenseDialog(false);
      loadDailyAccount(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving bank expenses:', error);
      setAlert({ type: 'error', message: 'Failed to save bank expenses' });
    }
  };

  const handleOpeningBalanceSubmit = async () => {
    const total = getTotal(openingDenominations);
    
    try {
      // Always check for the most recent account data before deciding
      let accountToUpdate = currentAccount;
      if (!accountToUpdate) {
        // Try to find existing account for this date and store
        try {
          accountToUpdate = await dailyAccountAPI.getByDateAndStore(date.format('YYYY-MM-DD'), storeId as number);
        } catch (error) {
          // If no account found, we'll create a new one
          accountToUpdate = null;
        }
      }
      
      if (accountToUpdate) {
        // Update existing account - only update opening_cash and denomination
        await dailyAccountAPI.update(accountToUpdate.id!, {
          opening_cash: total,
          opening_cash_denomination: JSON.stringify(openingDenominations),
          updated_by: 'system'
        });
      } else {
        // Create new account
        await dailyAccountAPI.create({
          date: date.format('YYYY-MM-DD'),
          store_id: storeId as number,
          opening_cash: total,
          opening_bank_balance: 0,
          total_expense: 0,
          closing_bank_balance: 0,
          total_bank_credits: 0,
          total_bank_debits: 0,
          closing_cash: 0,
          opening_cash_denomination: JSON.stringify(openingDenominations),
          closing_cash_denomination: undefined,
          cash_tally: undefined,
          created_by: 'system'
        });
      }
      
      setAlert({ type: 'success', message: 'Opening balance saved successfully' });
      setOpeningBalanceDialog(false);
      loadDailyAccount(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving opening balance:', error);
      setAlert({ type: 'error', message: 'Failed to save opening balance' });
    }
  };

  const handleClosingBalanceSubmit = async () => {
    const total = getTotal(closingDenominations);
    
    try {
      // Always check for the most recent account data before deciding
      let accountToUpdate = currentAccount;
      if (!accountToUpdate) {
        // Try to find existing account for this date and store
        try {
          accountToUpdate = await dailyAccountAPI.getByDateAndStore(date.format('YYYY-MM-DD'), storeId as number);
        } catch (error) {
          // If no account found, we'll create a new one
          accountToUpdate = null;
        }
      }
      
      if (accountToUpdate) {
        // Update existing account - only update closing_cash and denomination
        await dailyAccountAPI.update(accountToUpdate.id!, {
          closing_cash: total,
          closing_cash_denomination: JSON.stringify(closingDenominations),
          updated_by: 'system'
        });
      } else {
        // Create new account
        await dailyAccountAPI.create({
          date: date.format('YYYY-MM-DD'),
          store_id: storeId as number,
          opening_cash: 0,
          opening_bank_balance: 0,
          total_expense: 0,
          closing_bank_balance: 0,
          total_bank_credits: 0,
          total_bank_debits: 0,
          closing_cash: total,
          opening_cash_denomination: undefined,
          closing_cash_denomination: JSON.stringify(closingDenominations),
          cash_tally: undefined,
          created_by: 'system'
        });
      }
      
      setAlert({ type: 'success', message: 'Closing balance saved successfully' });
      setClosingBalanceDialog(false);
      loadDailyAccount(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving closing balance:', error);
      setAlert({ type: 'error', message: 'Failed to save closing balance' });
    }
  };

  const resetDenominations = (isOpening: boolean) => {
    const resetDenominations = [
      { denomination: 2000, count: 0, total: 0 },
      { denomination: 1000, count: 0, total: 0 },
      { denomination: 500, count: 0, total: 0 },
      { denomination: 200, count: 0, total: 0 },
      { denomination: 100, count: 0, total: 0 },
      { denomination: 50, count: 0, total: 0 },
      { denomination: 20, count: 0, total: 0 },
      { denomination: 10, count: 0, total: 0 },
      { denomination: 5, count: 0, total: 0 },
      { denomination: 2, count: 0, total: 0 },
      { denomination: 1, count: 0, total: 0 },
    ];
    
    if (isOpening) {
      setOpeningDenominations(resetDenominations);
    } else {
      setClosingDenominations(resetDenominations);
    }
  };

  if (!selectedStore) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="textSecondary">
            Please select a store to continue
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Daily Account Management
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Select Date"
                value={date}
                onChange={(newDate) => setDate(newDate || dayjs())}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" color="textSecondary">
                Store: {selectedStore.store_name}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<AccountBalance />}
                onClick={handleOpeningBalanceClick}
                sx={{ 
                  height: 80, 
                  fontSize: '1.2rem',
                  backgroundColor: '#4caf50',
                  '&:hover': { backgroundColor: '#45a049' }
                }}
              >
                Opening Balance: ₹{currentAccount?.opening_cash || 0}
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<AccountBalanceWallet />}
                onClick={handleClosingBalanceClick}
                sx={{ 
                  height: 80, 
                  fontSize: '1.2rem',
                  backgroundColor: '#ff9800',
                  '&:hover': { backgroundColor: '#f57c00' }
                }}
              >
                Closing Balance: ₹{currentAccount?.closing_cash || 0}
              </Button>
            </Grid>
          </Grid>

          {loading && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          )}

                     {currentAccount && (
             <Card variant="outlined" sx={{ mt: 2 }}>
               <CardContent>
                 <Typography variant="h6" gutterBottom>
                   Current Account Details
                 </Typography>
                 <Grid container spacing={2}>
                   <Grid item xs={6} md={6}>
                     <Typography variant="body2" color="textSecondary">Opening Cash</Typography>
                     <Typography variant="h6">₹{currentAccount.opening_cash}</Typography>
                     <Typography variant="caption" color="textSecondary" display="block">
                       {formatDenominations(parseDenominations(currentAccount.opening_cash_denomination))}
                     </Typography>
                   </Grid>
                   <Grid item xs={6} md={6}>
                     <Typography variant="body2" color="textSecondary">Closing Cash</Typography>
                     <Typography variant="h6">₹{currentAccount.closing_cash}</Typography>
                     <Typography variant="caption" color="textSecondary" display="block">
                       {formatDenominations(parseDenominations(currentAccount.closing_cash_denomination))}
                     </Typography>
                   </Grid>
                 </Grid>
               </CardContent>
             </Card>
           )}

           {/* Bank Expense Details Section */}
           {currentAccount && currentAccount.bank_expense && (
             <Card variant="outlined" sx={{ mt: 2 }}>
               <CardContent>
                 <Typography variant="h6" gutterBottom>
                   Bank Expense Details
                 </Typography>
                 {(() => {
                   try {
                     const expenseData = JSON.parse(currentAccount.bank_expense);
                     
                     return (
                       <Grid container spacing={2}>
                         <Grid item xs={12} md={6}>
                           <Typography variant="body2" color="textSecondary">Total Bank Expense</Typography>
                           <Typography variant="h6" color="error.main">
                             ₹{expenseData.total_bank_expense.toLocaleString()}
                           </Typography>
                         </Grid>
                         <Grid item xs={12}>
                           <Typography variant="body2" color="textSecondary" gutterBottom>
                             Expense Items:
                           </Typography>
                           <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                             {expenseData.items.map((item: BankExpenseItem, index: number) => (
                               <Typography 
                                 key={index} 
                                 variant="caption" 
                                 color="error.main"
                                 display="block"
                                 sx={{ mb: 0.5 }}
                               >
                                 - {item.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: ₹{item.amount.toLocaleString()}
                               </Typography>
                             ))}
                           </Box>
                         </Grid>
                       </Grid>
                     );
                   } catch (error) {
                     return (
                       <Typography variant="body2" color="error.main">
                         Error parsing bank expense data
                       </Typography>
                     );
                   }
                 })()}
               </CardContent>
             </Card>
           )}

           {/* Tally Details Section */}
           {currentAccount && currentAccount.cash_tally && (
             <Card variant="outlined" sx={{ mt: 2 }}>
               <CardContent>
                 <Typography variant="h6" gutterBottom>
                   Tally Details
                 </Typography>
                 {(() => {
                   try {
                     const tallyData = JSON.parse(currentAccount.cash_tally);
                     const expectedDifference = currentAccount.closing_cash - currentAccount.opening_cash;
                     const netAmount = tallyData.net_amount;
                     const difference = Math.abs(netAmount - expectedDifference);
                     
                     let status = '';
                     let statusColor = '';
                     
                     if (Math.abs(netAmount - expectedDifference) < 0.01) {
                       status = 'Accounts Tallied';
                       statusColor = 'success.main';
                     } else if (netAmount < expectedDifference) {
                       status = `Excess Cash: ₹${difference.toLocaleString()}`;
                       statusColor = 'warning.main';
                     } else {
                       status = `Cash Missing: ₹${difference.toLocaleString()}`;
                       statusColor = 'error.main';
                     }
                     
                     return (
                       <Grid container spacing={2}>
                         <Grid item xs={12} md={3}>
                           <Typography variant="body2" color="textSecondary">Total Credit</Typography>
                           <Typography variant="h6" color="success.main">
                             ₹{tallyData.total_credit.toLocaleString()}
                           </Typography>
                         </Grid>
                         <Grid item xs={12} md={3}>
                           <Typography variant="body2" color="textSecondary">Total Debit</Typography>
                           <Typography variant="h6" color="error.main">
                             ₹{tallyData.total_debit.toLocaleString()}
                           </Typography>
                         </Grid>
                         <Grid item xs={12} md={3}>
                           <Typography variant="body2" color="textSecondary">Net Amount</Typography>
                           <Typography variant="h6" color="primary.main">
                             ₹{tallyData.net_amount.toLocaleString()}
                           </Typography>
                         </Grid>
                         <Grid item xs={12} md={3}>
                           <Typography variant="body2" color="textSecondary">Status</Typography>
                           <Typography variant="h6" color={statusColor}>
                             {status}
                           </Typography>
                         </Grid>
                         <Grid item xs={12}>
                           <Typography variant="body2" color="textSecondary" gutterBottom>
                             Tally Items:
                           </Typography>
                           <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                                           {tallyData.items.map((item: CashTallyItem, index: number) => (
                                <Typography 
                                  key={index} 
                                  variant="caption" 
                                  color={item.type === 'credit' ? 'success.main' : 'error.main'}
                                  display="block"
                                  sx={{ mb: 0.5 }}
                                >
                                  {item.type === 'credit' ? '+' : '-'} {item.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: ₹{item.amount.toLocaleString()}
                                </Typography>
                              ))}
                           </Box>
                         </Grid>
                       </Grid>
                     );
                   } catch (error) {
                     return (
                       <Typography variant="body2" color="error.main">
                         Error parsing tally data
                       </Typography>
                     );
                   }
                 })()}
               </CardContent>
             </Card>
           )}

                     {/* Cash Tally and Bank Expense Buttons */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<Receipt />}
                onClick={handleCashTallyClick}
                sx={{ 
                  height: 80, 
                  fontSize: '1.2rem',
                  backgroundColor: '#2196f3',
                  '&:hover': { backgroundColor: '#1976d2' }
                }}
              >
                Cash Tally
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<CreditCard />}
                onClick={handleBankExpenseClick}
                sx={{ 
                  height: 80, 
                  fontSize: '1.2rem',
                  backgroundColor: '#9c27b0',
                  '&:hover': { backgroundColor: '#7b1fa2' }
                }}
              >
                Bank/Cheque Expense
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Opening Balance Dialog */}
      <Dialog 
        open={openingBalanceDialog} 
        onClose={() => setOpeningBalanceDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Opening Balance - {date.format('DD/MM/YYYY')}
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Denomination</TableCell>
                  <TableCell align="center">Count</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {openingDenominations.map((item, index) => (
                  <TableRow key={item.denomination}>
                    <TableCell>₹{item.denomination.toLocaleString()}</TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        value={item.count}
                        onChange={(e) => updateDenominationCount(index, parseInt(e.target.value) || 0, true)}
                        inputProps={{ min: 0 }}
                        size="small"
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell align="right">₹{item.total.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="h6">Total</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="h6" color="primary">
                      ₹{getTotal(openingDenominations).toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resetDenominations(true)}>Reset</Button>
          <Button onClick={() => setOpeningBalanceDialog(false)}>Cancel</Button>
          <Button onClick={handleOpeningBalanceSubmit} variant="contained" color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Closing Balance Dialog */}
      <Dialog 
        open={closingBalanceDialog} 
        onClose={() => setClosingBalanceDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Closing Balance - {date.format('DD/MM/YYYY')}
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Denomination</TableCell>
                  <TableCell align="center">Count</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {closingDenominations.map((item, index) => (
                  <TableRow key={item.denomination}>
                    <TableCell>₹{item.denomination.toLocaleString()}</TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        value={item.count}
                        onChange={(e) => updateDenominationCount(index, parseInt(e.target.value) || 0, false)}
                        inputProps={{ min: 0 }}
                        size="small"
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell align="right">₹{item.total.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="h6">Total</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="h6" color="primary">
                      ₹{getTotal(closingDenominations).toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resetDenominations(false)}>Reset</Button>
          <Button onClick={() => setClosingBalanceDialog(false)}>Cancel</Button>
          <Button onClick={handleClosingBalanceSubmit} variant="contained" color="primary">
            Submit
          </Button>
                 </DialogActions>
       </Dialog>

       {/* Cash Tally Dialog */}
       <Dialog 
         open={cashTallyDialog} 
         onClose={() => setCashTallyDialog(false)}
         maxWidth="lg"
         fullWidth
       >
         <DialogTitle>
           Cash Tally - {date.format('DD/MM/YYYY')}
         </DialogTitle>
         <DialogContent>
           <Grid container spacing={3} sx={{ mt: 2 }}>
             {/* Credit Types Section */}
             <Grid item xs={12} md={6}>
               <Typography variant="h6" color="success.main" gutterBottom>
                 Cash Credit Types (Green)
               </Typography>
               <Grid container spacing={2}>
                 <Grid item xs={8}>
                   <TextField
                     select
                     fullWidth
                     label="Credit Type"
                     value={selectedCreditType}
                     onChange={(e) => setSelectedCreditType(e.target.value)}
                     size="small"
                   >
                     {cashCreditTypes.map((type) => (
                       <MenuItem key={type} value={type}>
                         {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                       </MenuItem>
                     ))}
                   </TextField>
                 </Grid>
                 <Grid item xs={4}>
                   <TextField
                     type="number"
                     fullWidth
                     label="Amount"
                     value={creditAmount}
                     onChange={(e) => setCreditAmount(Number(e.target.value) || 0)}
                     size="small"
                     inputProps={{ min: 0 }}
                   />
                 </Grid>
                 <Grid item xs={12}>
                   <Button
                     variant="contained"
                     color="success"
                     onClick={addCreditItem}
                     disabled={!selectedCreditType || creditAmount <= 0}
                     fullWidth
                   >
                     Add Credit Item
                   </Button>
                 </Grid>
               </Grid>
             </Grid>

             {/* Debit Types Section */}
             <Grid item xs={12} md={6}>
               <Typography variant="h6" color="error.main" gutterBottom>
                 Cash Debit Types (Red)
               </Typography>
               <Grid container spacing={2}>
                 <Grid item xs={8}>
                   <TextField
                     select
                     fullWidth
                     label="Debit Type"
                     value={selectedDebitType}
                     onChange={(e) => setSelectedDebitType(e.target.value)}
                     size="small"
                   >
                     {cashDebitTypes.map((type) => (
                       <MenuItem key={type} value={type}>
                         {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                       </MenuItem>
                     ))}
                   </TextField>
                 </Grid>
                 <Grid item xs={4}>
                   <TextField
                     type="number"
                     fullWidth
                     label="Amount"
                     value={debitAmount}
                     onChange={(e) => setDebitAmount(Number(e.target.value) || 0)}
                     size="small"
                     inputProps={{ min: 0 }}
                   />
                 </Grid>
                 <Grid item xs={12}>
                   <Button
                     variant="contained"
                     color="error"
                     onClick={addDebitItem}
                     disabled={!selectedDebitType || debitAmount <= 0}
                     fullWidth
                   >
                     Add Debit Item
                   </Button>
                 </Grid>
               </Grid>
             </Grid>

             {/* Cash Tally Table */}
             <Grid item xs={12}>
               <Typography variant="h6" gutterBottom>
                 Cash Tally Items
               </Typography>
               <TableContainer component={Paper}>
                 <Table>
                   <TableHead>
                     <TableRow>
                       <TableCell>Type</TableCell>
                       <TableCell>Category</TableCell>
                       <TableCell align="right">Amount</TableCell>
                       <TableCell align="center">Action</TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                     {cashTallyItems.map((item, index) => (
                       <TableRow key={index}>
                         <TableCell>
                           <Typography
                             color={item.type === 'credit' ? 'success.main' : 'error.main'}
                           >
                             {item.type === 'credit' ? 'Credit' : 'Debit'}
                           </Typography>
                         </TableCell>
                         <TableCell>
                           {item.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                         </TableCell>
                         <TableCell align="right">
                           <Typography
                             color={item.type === 'credit' ? 'success.main' : 'error.main'}
                           >
                             ₹{item.amount.toLocaleString()}
                           </Typography>
                         </TableCell>
                         <TableCell align="center">
                           <Button
                             size="small"
                             color="error"
                             onClick={() => removeTallyItem(index)}
                           >
                             Remove
                           </Button>
                         </TableCell>
                       </TableRow>
                     ))}
                   </TableBody>
                 </Table>
               </TableContainer>
             </Grid>

             {/* Summary and Validation */}
             <Grid item xs={12}>
               <Card variant="outlined" sx={{ p: 2 }}>
                 <Grid container spacing={2}>
                   <Grid item xs={12} md={4}>
                     <Typography variant="body2" color="textSecondary">Total Credit</Typography>
                     <Typography variant="h6" color="success.main">
                       ₹{calculateCashTallyTotal().total_credit.toLocaleString()}
                     </Typography>
                   </Grid>
                   <Grid item xs={12} md={4}>
                     <Typography variant="body2" color="textSecondary">Total Debit</Typography>
                     <Typography variant="h6" color="error.main">
                       ₹{calculateCashTallyTotal().total_debit.toLocaleString()}
                     </Typography>
                   </Grid>
                   <Grid item xs={12} md={4}>
                     <Typography variant="body2" color="textSecondary">Net Amount</Typography>
                     <Typography variant="h6" color="primary.main">
                       ₹{calculateCashTallyTotal().net_amount.toLocaleString()}
                     </Typography>
                   </Grid>
                 </Grid>
                 
                 <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                   <Typography variant="body2" color="textSecondary">
                     Expected Difference (Closing - Opening): ₹{getExpectedDifference().toLocaleString()}
                   </Typography>
                   <Typography 
                     variant="h6" 
                     color={Math.abs(calculateCashTallyTotal().net_amount - getExpectedDifference()) < 0.01 ? 'success.main' : 'error.main'}
                     sx={{ mt: 1 }}
                   >
                     {Math.abs(calculateCashTallyTotal().net_amount - getExpectedDifference()) < 0.01 
                       ? '✓ Amounts Match' 
                       : `≠ Difference: ₹${Math.abs(calculateCashTallyTotal().net_amount - getExpectedDifference()).toLocaleString()}`
                     }
                   </Typography>
                 </Box>
               </Card>
             </Grid>
           </Grid>
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setCashTallyDialog(false)}>Cancel</Button>
           <Button onClick={handleCashTallySubmit} variant="contained" color="primary">
             Submit
           </Button>
         </DialogActions>
       </Dialog>

       {/* Bank Expense Dialog */}
       <Dialog 
         open={bankExpenseDialog} 
         onClose={() => setBankExpenseDialog(false)}
         maxWidth="md"
         fullWidth
       >
         <DialogTitle>
           Bank/Cheque Expense - {date.format('DD/MM/YYYY')}
         </DialogTitle>
         <DialogContent>
           <Grid container spacing={3} sx={{ mt: 2 }}>
             {/* Expense Input Section */}
             <Grid item xs={12}>
               <Typography variant="h6" color="error.main" gutterBottom>
                 Bank/Cheque Expense Types
               </Typography>
               <Grid container spacing={2}>
                 <Grid item xs={8}>
                   <TextField
                     select
                     fullWidth
                     label="Expense Type"
                     value={selectedExpenseType}
                     onChange={(e) => setSelectedExpenseType(e.target.value)}
                     size="small"
                   >
                     {bankExpenseTypes.map((type) => (
                       <MenuItem key={type} value={type}>
                         {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                       </MenuItem>
                     ))}
                   </TextField>
                 </Grid>
                 <Grid item xs={4}>
                   <TextField
                     type="number"
                     fullWidth
                     label="Amount"
                     value={expenseAmount}
                     onChange={(e) => setExpenseAmount(Number(e.target.value) || 0)}
                     size="small"
                     inputProps={{ min: 0 }}
                   />
                 </Grid>
                 <Grid item xs={12}>
                   <Button
                     variant="contained"
                     color="error"
                     onClick={addExpenseItem}
                     disabled={!selectedExpenseType || expenseAmount <= 0}
                     fullWidth
                   >
                     Add Expense Item
                   </Button>
                 </Grid>
               </Grid>
             </Grid>

             {/* Bank Expense Table */}
             <Grid item xs={12}>
               <Typography variant="h6" gutterBottom>
                 Bank Expense Items
               </Typography>
               <TableContainer component={Paper}>
                 <Table>
                   <TableHead>
                     <TableRow>
                       <TableCell>Category</TableCell>
                       <TableCell align="right">Amount</TableCell>
                       <TableCell align="center">Action</TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                     {bankExpenseItems.map((item, index) => (
                       <TableRow key={index}>
                         <TableCell>
                           {item.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                         </TableCell>
                         <TableCell align="right">
                           <Typography color="error.main">
                             ₹{item.amount.toLocaleString()}
                           </Typography>
                         </TableCell>
                         <TableCell align="center">
                           <Button
                             size="small"
                             color="error"
                             onClick={() => removeExpenseItem(index)}
                           >
                             Remove
                           </Button>
                         </TableCell>
                       </TableRow>
                     ))}
                   </TableBody>
                 </Table>
               </TableContainer>
             </Grid>

             {/* Summary */}
             <Grid item xs={12}>
               <Card variant="outlined" sx={{ p: 2 }}>
                 <Typography variant="body2" color="textSecondary">Total Bank Expense</Typography>
                 <Typography variant="h6" color="error.main">
                   ₹{calculateBankExpenseTotal().total_bank_expense.toLocaleString()}
                 </Typography>
               </Card>
             </Grid>
           </Grid>
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setBankExpenseDialog(false)}>Cancel</Button>
           <Button onClick={handleBankExpenseSubmit} variant="contained" color="primary">
             Submit
           </Button>
         </DialogActions>
       </Dialog>
     </Box>
   );
 };

export default DailyAccountComponent;
